import { i18n } from '@/shared/i18n';
import type { ConnectorPlatform } from '@/entities/media-connector';

/**
 * A connected ad account a Job can be published into.
 *
 * Mirrors the fields of `MediaConnector` the binding needs, so the picker reads
 * one shape whether it is fed from the connectors endpoint or a fixture.
 */
export interface ConnectorAccount {
  id: string;
  platform: ConnectorPlatform;
  title: string;
  externalAccountId: string;
  externalAccountName: string;
  status: ConnectorHealth;
}

/**
 * How usable a connected account is, as the platform reports it.
 *
 * Anything but `connected` blocks publishing, but they are not the same problem and
 * do not have the same fix, so the screen keeps them apart rather than saying
 * "reconnect" to all of them.
 */
export type ConnectorHealth = 'connected' | 'pending' | 'disconnected' | 'expired' | 'error';

/**
 * The states that genuinely stop a publish.
 *
 * `error` is not one of them. It is written by any failed sync, and a sync reads through
 * a different API than a publish — on Google, reporting goes through Bid Manager and
 * writes through Display & Video, so reporting can be switched off on the cloud project
 * while publishing works perfectly. Treating it as fatal blocked publishes that would
 * have gone through, and sent people to reconnect an account with nothing wrong with it.
 */
/**
 * What a connection can currently do, mirrored from the API.
 *
 * Declared here rather than imported from the connector entity for the same reason
 * {@link ConnectorHealth} is: this is the delivery screen's own vocabulary, and entities
 * do not reach into each other.
 */
export type CapabilityState = 'unknown' | 'ok' | 'failing';

export interface ConnectorCapability {
  state: CapabilityState;
  checkedAt: string | null;
  /** The platform's own words, which name the fix far better than we could. */
  error: string | null;
}

export interface ConnectorCapabilities {
  signIn: ConnectorCapability;
  publishing: ConnectorCapability;
  reporting: ConnectorCapability;
}

export const BLOCKING_CONNECTOR_HEALTH: ReadonlySet<ConnectorHealth> = new Set([
  'pending',
  'disconnected',
  'expired',
]);

/**
 * What to tell someone whose account is in this state, and what it is called.
 *
 * Getters, not values: a resolved label here would freeze English at import.
 */
export const CONNECTOR_HEALTH_LABELS: Record<ConnectorHealth, string> = {
  get connected() {
    return i18n.t('projects:mediaCampaign.connectorHealth.connected');
  },
  get pending() {
    return i18n.t('projects:mediaCampaign.connectorHealth.pending');
  },
  get disconnected() {
    return i18n.t('projects:mediaCampaign.connectorHealth.disconnected');
  },
  get expired() {
    return i18n.t('projects:mediaCampaign.connectorHealth.expired');
  },
  get error() {
    return i18n.t('projects:mediaCampaign.connectorHealth.error');
  },
};

export const CONNECTOR_HEALTH_ADVICE: Record<Exclude<ConnectorHealth, 'connected'>, string> = {
  get pending() {
    return i18n.t('projects:mediaCampaign.connectorAdvice.pending');
  },
  get disconnected() {
    return i18n.t('projects:mediaCampaign.connectorAdvice.disconnected');
  },
  get expired() {
    return i18n.t('projects:mediaCampaign.connectorAdvice.expired');
  },
  get error() {
    return i18n.t('projects:mediaCampaign.connectorAdvice.error');
  },
};
